import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useProperty,
  usePropertyField,
  useSecondaryColor,
  useTokens,
} from '@vibesites/runtime/react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

// Relative luminance (0 = black, 1 = white) for a CSS hex / rgb() color.
// Returns null when the color can't be parsed.
function relativeLuminance(color: string | null | undefined): number | null {
  if (!color) return null;
  const c = color.trim().toLowerCase();
  let r: number, g: number, b: number;

  const hexMatch = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const rgbMatch = c.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!rgbMatch) return null;
    r = parseInt(rgbMatch[1], 10);
    g = parseInt(rgbMatch[2], 10);
    b = parseInt(rgbMatch[3], 10);
  }

  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function isDarkColor(color: string | null | undefined): boolean {
  const lum = relativeLuminance(color);
  return lum !== null && lum <= 0.5;
}

function getDistanceLabel(poi: POI): string | null {
  if (hasVal(poi.poi_miles_from_property)) {
    const trimmed = poi.poi_miles_from_property.trim();
    return /mi|km|ft$/i.test(trimmed) ? trimmed : `${trimmed} mi`;
  }
  if (hasVal(poi.poi_driving_minutes)) {
    return `${poi.poi_driving_minutes.trim()} min drive`;
  }
  if (hasVal(poi.poi_walking_minutes)) {
    return `${poi.poi_walking_minutes.trim()} min walk`;
  }
  return null;
}

// ─── Google Maps loader ──────────────────────────────────────────────────────

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { GOOGLE_MAPS_API_KEY?: string } }).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if ((window as unknown as { google?: { maps?: { Map?: unknown } } }).google?.maps?.Map) {
    return Promise.resolve();
  }
  if (mapsLoaderPromise) return mapsLoaderPromise;

  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) {
      reject(new Error('No Google Maps API key'));
      return;
    }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as unknown as Record<string, unknown>)[cb] = () => {
      delete (window as unknown as Record<string, unknown>)[cb];
      resolve();
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => {
      mapsLoaderPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };
    document.head.appendChild(s);
  });

  return mapsLoaderPromise;
}

// ─── Marker SVGs ─────────────────────────────────────────────────────────────

function whiteCircleMarker(selected: boolean, primaryColor: string): string {
  const size = selected ? 40 : 28;
  const stroke = selected ? (primaryColor || '#1c2433') : '#888';
  const strokeW = selected ? 3 : 1.5;
  // When selected, fill with the property primary color for a clear highlight;
  // otherwise keep the neutral white pin.
  const fill = selected ? (primaryColor || '#1c2433') : '#ffffff';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - strokeW}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// ─── HTML map overlays (property pin + selected POI chip) ────────────────────

// A google.maps.OverlayView that pins an arbitrary DOM element to a lat/lng,
// anchored by its bottom-center (so the tip of a pin/chip lands on the point).
// Returns the overlay instance (call .setMap(null) to remove).
function createPositionedOverlay(
  g: any,
  map: any,
  lat: number,
  lng: number,
  el: HTMLElement,
): any {
  class PositionedOverlay extends g.OverlayView {
    el: HTMLElement;
    lat: number;
    lng: number;
    constructor() {
      super();
      this.el = el;
      this.lat = lat;
      this.lng = lng;
    }
    onAdd() {
      this.el.style.position = 'absolute';
      // floatPane renders above markers so chips/pins are never occluded.
      this.getPanes().floatPane.appendChild(this.el);
    }
    draw() {
      const proj = this.getProjection();
      if (!proj) return;
      const p = proj.fromLatLngToDivPixel(new g.LatLng(this.lat, this.lng));
      if (p) {
        this.el.style.left = `${p.x}px`;
        this.el.style.top = `${p.y}px`;
      }
    }
    onRemove() {
      if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
    }
  }
  const overlay = new PositionedOverlay();
  overlay.setMap(map);
  return overlay;
}

// Branded property marker: the property's mono logo over a primary-color pill.
// Falls back to the property name text when no mono logo is available.
function buildPropertyMarkerEl(
  primaryColor: string,
  logoUrl: string | null,
  propertyName: string,
): HTMLElement {
  const color = primaryColor || '#1c2433';
  const wrap = document.createElement('div');
  wrap.style.transform = 'translate(-50%, -100%)';
  wrap.style.pointerEvents = 'none';

  const pill = document.createElement('div');
  pill.style.cssText = `display:flex;align-items:center;justify-content:center;background:${color};border:2px solid #ffffff;border-radius:6px;padding:6px 12px;box-shadow:0 2px 8px rgba(0,0,0,0.3);min-height:24px;`;

  if (logoUrl) {
    const img = document.createElement('img');
    img.src = logoUrl;
    img.alt = propertyName;
    img.style.cssText = 'height:22px;width:auto;max-width:120px;display:block;';
    pill.appendChild(img);
  } else {
    const span = document.createElement('span');
    span.textContent = (propertyName || 'Property').toUpperCase();
    span.style.cssText =
      'font-family:Helvetica,Arial,sans-serif;font-size:12px;font-weight:700;letter-spacing:1px;color:#ffffff;white-space:nowrap;';
    pill.appendChild(span);
  }

  const tip = document.createElement('div');
  tip.style.cssText = `width:0;height:0;margin:0 auto;border-left:6px solid transparent;border-right:6px solid transparent;border-top:7px solid ${color};`;

  wrap.appendChild(pill);
  wrap.appendChild(tip);
  return wrap;
}

// Selected-POI chip: a small label that floats above the active marker.
function buildPoiChipEl(name: string, distance: string | null): HTMLElement {
  const wrap = document.createElement('div');
  // Lift clear of the 40px selected marker dot.
  wrap.style.transform = 'translate(-50%, calc(-100% - 22px))';
  wrap.style.pointerEvents = 'none';

  const chip = document.createElement('div');
  chip.style.cssText =
    'background:#ffffff;border-radius:8px;padding:6px 10px;box-shadow:0 2px 10px rgba(0,0,0,0.22);max-width:220px;text-align:center;';

  const nameEl = document.createElement('div');
  nameEl.textContent = name || 'Untitled place';
  nameEl.style.cssText =
    'font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:700;color:#1c2433;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
  chip.appendChild(nameEl);

  if (distance) {
    const distEl = document.createElement('div');
    distEl.textContent = distance;
    distEl.style.cssText =
      'font-family:Helvetica,Arial,sans-serif;font-size:11px;color:#6b7280;margin-top:1px;white-space:nowrap;';
    chip.appendChild(distEl);
  }

  const tip = document.createElement('div');
  tip.style.cssText =
    'width:0;height:0;margin:0 auto;border-left:6px solid transparent;border-right:6px solid transparent;border-top:7px solid #ffffff;';

  wrap.appendChild(chip);
  wrap.appendChild(tip);
  return wrap;
}

function buildMapStyles() {
  return [
    { elementType: 'geometry', stylers: [{ color: '#f7f3ec' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f7f3ec' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#efeae0' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#7a7a7a' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e2e8d4' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bfe1f0' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ─── Google Map sub-component ────────────────────────────────────────────────

interface MapProps {
  propertyLat: number;
  propertyLng: number;
  propertyName: string;
  primaryColor: string;
  monoLogoUrl: string | null;
  pois: POI[];
  selectedIndex: number | null;
  onMarkerClick: (index: number) => void;
}

function NeighborhoodMapGoogle({
  propertyLat,
  propertyLng,
  propertyName,
  primaryColor,
  monoLogoUrl,
  pois,
  selectedIndex,
  onMarkerClick,
}: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Use `unknown` here; Google Maps types not bundled
  const mapRef = useRef<unknown>(null);
  const markersRef = useRef<{ marker: any; poiIndex: number }[]>([]);
  const propertyOverlayRef = useRef<any>(null);
  const chipOverlayRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialise map once
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const g = (window as any).google.maps;
        const map = new g.Map(containerRef.current, {
          center: { lat: propertyLat, lng: propertyLng },
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: true,
          streetViewControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
          fullscreenControl: false,
          zoomControl: false,
          styles: buildMapStyles(),
        });

        // Branded property marker: mono logo over the primary color.
        propertyOverlayRef.current = createPositionedOverlay(
          g,
          map,
          propertyLat,
          propertyLng,
          buildPropertyMarkerEl(primaryColor, monoLogoUrl, propertyName),
        );

        mapRef.current = map;
        setReady(true);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load map');
      });
    return () => {
      cancelled = true;
      if (propertyOverlayRef.current) {
        propertyOverlayRef.current.setMap(null);
        propertyOverlayRef.current = null;
      }
    };
  }, [propertyLat, propertyLng, propertyName, primaryColor, monoLogoUrl]);

  // Re-render POI markers whenever the visible POI list changes
  const syncMarkers = useCallback(() => {
    const map = mapRef.current as any;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    // Clear old
    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;

    pois.forEach((poi, idx) => {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) return;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const isSelected = selectedIndex === idx;
      const size = isSelected ? 40 : 28;

      const marker = new g.Marker({
        position: pos,
        map,
        title: poi.poi_name || '',
        zIndex: isSelected ? 998 : 100,
        icon: {
          url: whiteCircleMarker(isSelected, primaryColor),
          scaledSize: new g.Size(size, size),
          anchor: new g.Point(size / 2, size / 2),
        },
      });

      marker.addListener('click', () => onMarkerClick(idx));
      markersRef.current.push({ marker, poiIndex: idx });
    });

    if (hasPoiBounds) {
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    }
  }, [pois, selectedIndex, propertyLat, propertyLng, primaryColor, onMarkerClick]);

  useEffect(() => {
    if (!ready) return;
    syncMarkers();
  }, [ready, syncMarkers]);

  // Pan + zoom to the selected POI
  useEffect(() => {
    if (!ready || selectedIndex === null) return;
    const map = mapRef.current as any;
    const g = (window as any).google?.maps;
    if (!map || !g) return;
    const poi = pois[selectedIndex];
    const lat = parseFloat(poi?.poi_latitude || '');
    const lng = parseFloat(poi?.poi_longitude || '');
    if (isNaN(lat) || isNaN(lng)) return;
    map.panTo({ lat, lng });
    if (map.getZoom() < 15) map.setZoom(15);
  }, [ready, selectedIndex, pois]);

  // Floating name chip above the selected POI marker
  useEffect(() => {
    const map = mapRef.current as any;
    const g = (window as any).google?.maps;

    // Always clear the previous chip first.
    if (chipOverlayRef.current) {
      chipOverlayRef.current.setMap(null);
      chipOverlayRef.current = null;
    }
    if (!ready || !map || !g || selectedIndex === null) return;

    const poi = pois[selectedIndex];
    const lat = parseFloat(poi?.poi_latitude || '');
    const lng = parseFloat(poi?.poi_longitude || '');
    if (isNaN(lat) || isNaN(lng)) return;

    chipOverlayRef.current = createPositionedOverlay(
      g,
      map,
      lat,
      lng,
      buildPoiChipEl(poi.poi_name || '', getDistanceLabel(poi)),
    );

    return () => {
      if (chipOverlayRef.current) {
        chipOverlayRef.current.setMap(null);
        chipOverlayRef.current = null;
      }
    };
  }, [ready, selectedIndex, pois]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--vibe-color-surface)] text-[var(--vibe-color-text-muted)] font-body text-sm">
        Map unavailable — check the Google Maps API key.
      </div>
    );
  }

  return <div ref={containerRef} className="w-full h-full bg-[#f7f3ec]" />;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function NeighborhoodMap() {
  const property = useProperty();
  const propLatRaw = usePropertyField('property_address_latitude') as string | null;
  const propLngRaw = usePropertyField('property_address_longitude') as string | null;
  const propertyNameRaw = usePropertyField('property_name') as string | null;

  const propLat = parseFloat(propLatRaw || '') || 0;
  const propLng = parseFloat(propLngRaw || '') || 0;
  const propertyName = propertyNameRaw || 'Property';
  const primaryColor =
    (property as Record<string, unknown> | null)?.['property_primary_color'] as string | undefined ||
    '#1c2433';

  // Property pin logo: pick the mono variant that contrasts with the primary
  // color (dark primary → white logo, light primary → black logo), matching the
  // Header's luminance-based logo selection.
  const monoWhiteRaw = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackRaw = usePropertyField('property_mono_black_logo') as string | null;
  const monoWhiteLogoUrl = hasVal(monoWhiteRaw) ? monoWhiteRaw : null;
  const monoBlackLogoUrl = hasVal(monoBlackRaw) ? monoBlackRaw : null;
  const preferWhiteLogo = isDarkColor(primaryColor);
  const monoLogoUrl = preferWhiteLogo
    ? monoWhiteLogoUrl || monoBlackLogoUrl
    : monoBlackLogoUrl || monoWhiteLogoUrl;

  // "View Details" link color: when text-on-secondary reads dark (e.g. #1a1a1a),
  // the secondary surface is light, so a light secondary color would be invisible
  // on the list's light background — use the dark text-on-secondary color instead.
  // Otherwise the secondary color reads fine and stays on brand.
  const secondaryColor = useSecondaryColor();
  const textOnSecondary = useTokens().color.textOnSecondary;
  const detailsLinkColor = isDarkColor(textOnSecondary) ? textOnSecondary : secondaryColor;

  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', {
    collection: 'neighborhood_pois',
  });

  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Unique categories present in the data (alphabetical)
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const p of pois) {
      const t = (p.poi_type || '').trim();
      if (t) set.add(t);
    }
    return Array.from(set).sort();
  }, [pois]);

  const filteredIndices = useMemo(() => {
    if (activeFilter === 'all') return pois.map((_, i) => i);
    return pois
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => (p.poi_type || '').trim() === activeFilter)
      .map(({ i }) => i);
  }, [pois, activeFilter]);

  const filteredPois = useMemo(
    () => filteredIndices.map((i) => pois[i]),
    [filteredIndices, pois],
  );

  // Reset selection when filter changes (keep if still visible)
  useEffect(() => {
    if (selectedIndex !== null && !filteredIndices.includes(selectedIndex)) {
      setSelectedIndex(null);
    }
  }, [filteredIndices, selectedIndex]);

  const handleCardClick = useCallback((origIndex: number) => {
    setSelectedIndex(origIndex);
  }, []);

  // Scroll the selected card into view in the side panel
  useEffect(() => {
    if (selectedIndex === null) return;
    const visiblePos = filteredIndices.indexOf(selectedIndex);
    if (visiblePos < 0) return;
    const card = cardRefs.current[visiblePos];
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedIndex, filteredIndices]);

  const hasPois = pois.length > 0;

  return (
    <section className="bg-white pt-12 md:pt-16">
      {/* ── Filter pills (centered, max-w-7xl) ─────────────────────────────── */}
      {hasPois && categories.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-10">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <FilterPill
              label="All"
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            />
            {categories.map((cat) => (
              <FilterPill
                key={cat}
                label={cat}
                active={activeFilter === cat}
                onClick={() => setActiveFilter(cat)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Map + side panel (full bleed) ──────────────────────────────────── */}
      {/* On mobile the side panel is hidden: users browse via the category pills
          and the on-map name chip that appears when a POI marker is tapped. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_400px] h-[460px] sm:h-[560px] lg:h-[720px]">
        {/* Left: Google Map */}
        <div className="relative w-full h-full bg-[#f7f3ec]">
          {hasPois ? (
            <NeighborhoodMapGoogle
              propertyLat={propLat || 27.7676}
              propertyLng={propLng || -82.6403}
              propertyName={propertyName}
              primaryColor={primaryColor}
              monoLogoUrl={monoLogoUrl}
              pois={filteredPois}
              selectedIndex={
                selectedIndex !== null ? filteredIndices.indexOf(selectedIndex) : null
              }
              onMarkerClick={(visibleIdx) => setSelectedIndex(filteredIndices[visibleIdx])}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[var(--vibe-color-text-muted)] font-body">
              No points of interest configured yet.
            </div>
          )}
        </div>

        {/* Right: Scrollable POI list (hidden on mobile) */}
        <div
          ref={listRef}
          className="hidden lg:block overflow-y-auto bg-white border-t lg:border-t-0 lg:border-l border-[var(--vibe-color-border)]"
        >
          {filteredPois.length === 0 ? (
            <div className="p-6 text-center text-[var(--vibe-color-text-muted)] font-body text-sm">
              No places match this filter.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--vibe-color-border)]">
              {filteredPois.map((poi, visibleIdx) => {
                const origIndex = filteredIndices[visibleIdx];
                const isSelected = selectedIndex === origIndex;
                const detailsUrl =
                  poi.poi_business_url ||
                  poi.poi_directions_map_url ||
                  null;
                const distance = getDistanceLabel(poi);

                return (
                  <li key={origIndex}>
                    <button
                      ref={(el) => (cardRefs.current[visibleIdx] = el)}
                      type="button"
                      onClick={() => handleCardClick(origIndex)}
                      className={`w-full text-left px-6 py-5 transition-colors ${
                        isSelected
                          ? 'bg-white shadow-sm'
                          : 'bg-white/40 hover:bg-white'
                      }`}
                    >
                      <h3 className="font-heading text-lg font-normal text-[var(--vibe-color-text)] mb-1.5 leading-tight">
                        {poi.poi_name || 'Untitled place'}
                      </h3>

                      {distance && (
                        <p className="font-body text-xs text-[var(--vibe-color-text-muted)] mb-2">
                          {distance} from property
                        </p>
                      )}

                      {detailsUrl ? (
                        <a
                          href={detailsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: detailsLinkColor }}
                          className="inline-flex items-center font-body text-sm underline underline-offset-2 transition-opacity hover:opacity-70"
                        >
                          View Details
                          <svg
                            className="w-3.5 h-3.5 ml-1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </a>
                      ) : (
                        <span className="inline-flex items-center font-body text-sm text-[var(--vibe-color-text-muted)]">
                          View Details
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Filter pill button ──────────────────────────────────────────────────────

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-6 py-2.5 rounded-md font-body text-sm font-medium transition-all duration-200 border ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-white text-[var(--vibe-color-text)] border-[var(--vibe-color-border)] hover:border-primary'
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {},
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodMap;
